import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Loader from "../../components/utils/Loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDollarSign, faTimes } from "@fortawesome/free-solid-svg-icons";
import OrderFlowEarningCalculator from "./OrderFlowEarningCalculator";

const MLMEarningEstimator = () => {
  const authState = useSelector((state) => state.authReducer);
  const navigate = useNavigate();

  const [userId] = useState(authState.user._id);
  const [userName] = useState(authState.user.name);
  const [plan, setPlan] = useState(20);
  const [maxLevel, setMaxLevel] = useState(8);
  const [refsPerLevel, setRefsPerLevel] = useState(5);
  const [amount, setAmount] = useState(20);
  const [errorMsg, setErrorMsg] = useState("");
  const [showEarningEstimator, setShowEarningEstimator] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!amount || isNaN(amount) || Number(amount) < 10 || Number(amount) > 99999) {
      setErrorMsg("Please enter a valid number between 10 to 99999 for the amount.");
      return;
    }

    if (!plan) {
      setErrorMsg("Please enter plan.");
      return;
    }

    const planNumber = Number(plan);
    if (planNumber !== 10 && planNumber !== 20) {
      setErrorMsg("Input 10 or 20 only in the plan");
      return;
    }

    setErrorMsg("");
    setShowEarningEstimator(true);
  };

  const handleCancel = () => navigate(-1);

  return (
    <div className="min-h-screen bg-black py-4 rounded-xl">
      <h3 className="text-xl lg:text-2xl font-bold text-yellow-200 text-center">Think5Whys ValueShare</h3>
      <h4 className="text-m lg:text-lg font-semibold text-pink-200 text-center mb-2">
        Community Referral Income
      </h4>

      <div className="shadow-lg rounded-xl bg-white/5 backdrop-blur-sm p-2 max-w-2xl mx-auto w-full border border-blue-500">
        {loading ? (
          <Loader />
        ) : (
          <form className="space-y-2">
            {/* Referral Plan */}
            <div className="flex flex-row sm:flex-row justify-between gap-2">
              <label htmlFor="plan" className="text-white font-medium">
                Referral Plan %{" "}
                <span className="text-gray-300 text-sm">(Input 10 or 20):</span>
              </label>
              <input
                type="number"
                id="plan"
                name="plan"
                value={plan}
                min={10}
                max={20}
                step={10}
                onChange={(e) => setPlan(e.target.value)}
                className={`w-28 h-10 px-3 py-2 border rounded-md text-center text-black focus:outline-none focus:ring-2 focus:ring-pink-500 ${
                  errorMsg ? "border-blue-500" : "border-gray-300"
                }`}
              />
            </div>

            {/* Amount */}
            <div className="flex flex-row sm:flex-row sm:items-center sm:justify-between gap-2">
              <label htmlFor="amount" className="text-white font-medium">
                Price / Spend:
              </label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={amount}
                min={10}
                max={99999}
                step={1000}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                className={`w-28 h-10 px-3 py-2 border rounded-md text-center text-black focus:outline-none focus:ring-2 focus:ring-pink-500 ${
                  errorMsg ? "border-blue-500" : "border-gray-300"
                }`}
              />
            </div>

            {errorMsg && <p className="text-red-400 text-sm text-center">{errorMsg}</p>}

            {/* Max Level */}
            <div className="flex flex-row sm:flex-row sm:items-center sm:justify-between gap-2">
              <label htmlFor="maxLevel" className="text-white font-medium">
                Max Level:
              </label>
              <select
                id="maxLevel"
                value={maxLevel}
                onChange={(e) => setMaxLevel(e.target.value)}
                className="w-16 h-10 px-3 py-2 border rounded-md text-black focus:outline-none focus:ring-2 focus:ring-pink-500"
              >
                {[...Array(8)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
            </div>

            {/* References per Level */}
            <div className="flex flex-row sm:flex-row sm:items-center sm:justify-between gap-2">
              <label htmlFor="refsPerLevel" className="text-white font-medium">
                References per Level:
              </label>
              <select
                id="refsPerLevel"
                value={refsPerLevel}
                onChange={(e) => setRefsPerLevel(e.target.value)}
                className="w-16 h-10 px-3 py-2 border rounded-md text-black focus:outline-none focus:ring-2 focus:ring-pink-500"
              >
                {[...Array(9)].map((_, i) => (
                  <option key={i + 2} value={i + 2}>
                    {i + 2}
                  </option>
                ))}
              </select>
            </div>

            {/* Buttons */}
            <div className="flex flex-row justify-center gap-3 mt-1">
              <button
                type="button"
                onClick={handleSubmit}
                className="w-40 bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-5 rounded-md transition duration-300"
              >
                <FontAwesomeIcon icon={faDollarSign} />
                <span className="px-4">Estimate</span>
              </button>

              <button
                type="button"
                onClick={handleCancel}
                className="w-40 bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-5 rounded-md transition duration-300"
              >
                <FontAwesomeIcon icon={faTimes} />
                <span className="px-4">Back</span>
              </button>
            </div>

            {/* Estimated Income Table */}
            {showEarningEstimator && (
              <div className="mt-0 text-center border-t border-blue-400 py-1">
                <p className="text-pink-200 text-sm sm:text-base mb-1">
                  Estimated income table for ₹{amount}.00, with {refsPerLevel} referrals per level, under a {plan}% referral incentive plan up to {maxLevel} levels.
                </p>
                <OrderFlowEarningCalculator
                  param1={amount}
                  param2={plan}
                  maxLevel={maxLevel}
                  refsPerLevel={refsPerLevel}
                />
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default MLMEarningEstimator;
